import React, { useEffect, useState, useCallback } from 'react';
import { auth, db } from '../firebase';
import { useNavigate } from 'react-router-dom';
import {
  doc,
  getDoc,
  collection,
  getDocs,
  updateDoc,
  deleteDoc,
} from 'firebase/firestore';
import { QRCodeCanvas } from 'qrcode.react';
import './CoupleDashboard.css';

export default function CoupleDashboard() {
  const [eventId, setEventId] = useState(null);
  const [eventInfo, setEventInfo] = useState({
    title: '',
    date: '',
    time: '',
    locationName: '',
    locationAddress: '',
    bannerUrl: '',
    eventCode: '',
  });
  const [mealOptions, setMealOptions] = useState([]);
  const [useMealOptions, setUseMealOptions] = useState(false);
  const [toggleMessage, setToggleMessage] = useState('');
  const [customQuestions, setCustomQuestions] = useState([]);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saveEventMessage, setSaveEventMessage] = useState('');
  const [saveMealsMessage, setSaveMealsMessage] = useState('');
  const [saveQuestionsMessage, setSaveQuestionsMessage] = useState('');
  const [deleteMessage, setDeleteMessage] = useState('');
  const [editOptionsMode, setEditOptionsMode] = useState(false);
  const [editCodeMode, setEditCodeMode] = useState(false);
  const [showQRCode, setShowQRCode] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const responsesPerPage = 5;
  const navigate = useNavigate();

  const handleNavigate = useCallback((path) => {
    navigate(path);
  }, [navigate]);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (!user) {
        handleNavigate('/login');
      } else {
        fetchCoupleEvent(user.uid);
      }
    });
    return () => unsubscribe();
  }, [handleNavigate]);

  const fetchCoupleEvent = async (uid) => {
    setLoading(true);
    try {
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (!userDoc.exists()) throw new Error('User not found');

      const eventIdFromDB = userDoc.data().eventId;
      setEventId(eventIdFromDB);

      const eventDoc = await getDoc(doc(db, 'events', eventIdFromDB));
      if (eventDoc.exists()) {
        const data = eventDoc.data();
        setEventInfo({
          title: data.title || '',
          date: data.date || '',
          time: data.time || '',
          locationName: data.locationName || data.location || '',
          locationAddress: data.locationAddress || '',
          bannerUrl: data.bannerUrl || '',
          eventCode: data.eventCode || '',
        });
        setMealOptions(data.mealOptions || []);
        setUseMealOptions(data.useMealOptions ?? (data.mealOptions?.length > 0));
        setCustomQuestions(data.customQuestions || []);
      }

      const rsvpSnapshot = await getDocs(collection(db, 'events', eventIdFromDB, 'rsvps'));
      const rsvps = rsvpSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      rsvps.sort((a, b) => {
        const aTime = a.createdAt?.toMillis?.() || a.id;
        const bTime = b.createdAt?.toMillis?.() || a.id;
        return bTime - aTime;
      });
      setResponses(rsvps);
    } catch (err) {
      console.error('Error loading event:', err);
      alert('Failed to load your event.');
    } finally {
      setLoading(false);
    }
  };

  const handleEventCodeChange = (value) => {
    setEventInfo((prev) => ({ ...prev, eventCode: value }));
  };

  const handleMealChange = (index, value) => {
    const updated = [...mealOptions];
    updated[index] = value;
    setMealOptions(updated);
  };

  const addMealOption = () => {
    setMealOptions([...mealOptions, '']);
  };

  const removeMealOption = (index) => {
    const updated = [...mealOptions];
    updated.splice(index, 1);
    setMealOptions(updated);
  };

  const handleQuestionChange = (index, value) => {
    const updated = [...customQuestions];
    updated[index] = value;
    setCustomQuestions(updated);
  };

  const addQuestion = () => {
    setCustomQuestions([...customQuestions, '']);
  };

  const removeQuestion = (index) => {
    if (customQuestions.length > 0) {
      const updated = [...customQuestions];
      updated.splice(index, 1);
      setCustomQuestions(updated);
    }
  };

  const handleToggleMealOptions = async (checked) => {
    if (!checked && mealOptions.length > 0) {
      const confirm = window.confirm('Disabling meal options will clear all meal selections. Continue?');
      if (!confirm) return;
    }
    setUseMealOptions(checked);
    setToggleMessage(checked ? 'Meal options enabled' : 'Meal options disabled');
    setTimeout(() => setToggleMessage(''), 2000);

    if (!eventId) return;
    try {
      const mealsToSave = checked ? mealOptions : [];
      await updateDoc(doc(db, 'events', eventId), {
        mealOptions: mealsToSave,
        useMealOptions: checked,
      });
      setMealOptions(mealsToSave);
    } catch (err) {
      console.error(err);
      alert('Failed to update meal options.');
    }
  };

  const handleSaveEvent = async () => {
    if (!eventId) return;
    try {
      await updateDoc(doc(db, 'events', eventId), {
        eventCode: eventInfo.eventCode,
      });
      setSaveEventMessage('Event code saved successfully!');
      setTimeout(() => setSaveEventMessage(''), 2000);
      setEditCodeMode(false);
    } catch (err) {
      console.error(err);
      alert('Failed to save event code.');
    }
  };

  const handleSaveMeals = async () => {
    if (!eventId || !useMealOptions) return;
    try {
      await updateDoc(doc(db, 'events', eventId), {
        mealOptions: mealOptions,
        useMealOptions,
      });
      setSaveMealsMessage('Meal options saved successfully!');
      setTimeout(() => setSaveMealsMessage(''), 2000);
    } catch (err) {
      console.error(err);
      alert('Failed to save meal options.');
    }
  };

  const handleSaveQuestions = async () => {
    if (!eventId) return;
    try {
      await updateDoc(doc(db, 'events', eventId), {
        customQuestions: customQuestions.filter(q => q.trim() !== ''),
      });
      setSaveQuestionsMessage('Custom questions saved successfully!');
      setTimeout(() => setSaveQuestionsMessage(''), 2000);
    } catch (err) {
      console.error(err);
      alert('Failed to save custom questions.');
    }
  };

  const handleDeleteRSVP = async (rsvpId) => {
    if (!eventId || !rsvpId) return;
    const confirm = window.confirm('Are you sure you want to delete this RSVP? This action cannot be undone.');
    if (!confirm) return;
    try {
      await deleteDoc(doc(db, 'events', eventId, 'rsvps', rsvpId));
      setResponses(prev => prev.filter(rsvp => rsvp.id !== rsvpId));
      setDeleteMessage('RSVP deleted successfully!');
      setTimeout(() => setDeleteMessage(''), 2000);
    } catch (err) {
      console.error('Error deleting RSVP:', err);
      alert('Failed to delete RSVP.');
    }
  };

  const handleLogout = async () => {
    await auth.signOut();
    handleNavigate('/login');
  };

  const handleViewEvent = () => {
    if (eventId) {
      handleNavigate(`/rsvp/${eventId}`);
    }
  };

  const handleMenuAction = (action) => {
    switch (action) {
      case 'home':
        setEditOptionsMode(false);
        setEditCodeMode(false);
        setShowQRCode(false);
        break;
      case 'view':
        handleViewEvent();
        break;
      case 'editCode':
        setEditCodeMode(true);
        setEditOptionsMode(false);
        setShowQRCode(false);
        break;
      case 'editOptions':
        setEditOptionsMode(true);
        setEditCodeMode(false);
        setShowQRCode(false);
        break;
      case 'viewQR':
        setShowQRCode(true);
        setEditCodeMode(false);
        setEditOptionsMode(false);
        break;
      case 'logout':
        handleLogout();
        break;
      default:
        break;
    }
  };

  const handleCopyLink = () => {
    const eventUrl = `${window.location.origin}/rsvp/${eventId}`;
    navigator.clipboard.writeText(eventUrl);
    alert('Event link copied to clipboard!');
  };

  const totalGuestsResponded = responses.length;
  const totalGuestsAttending = responses.reduce((sum, r) => r.isAttending ? sum + (r.numGuests || 1) : sum, 0);
  const yesResponses = responses.filter(r => r.isAttending).length;
  const noResponses = responses.filter(r => !r.isAttending).length;

  const mealCounts = {};
  if (useMealOptions && mealOptions?.length > 0) {
    mealOptions.forEach(opt => {
      if (opt.trim()) mealCounts[opt] = 0;
    });
    responses.forEach(rsvp => {
      if (rsvp.isAttending && Array.isArray(rsvp.mealSelections)) {
        rsvp.mealSelections.forEach(meal => {
          if (mealCounts.hasOwnProperty(meal)) mealCounts[meal]++;
        });
      }
    });
  }

  const totalPages = Math.ceil(responses.length / responsesPerPage);
  const paginatedResponses = responses.slice(
    (currentPage - 1) * responsesPerPage,
    currentPage * responsesPerPage
  );

  if (loading) return (
    <div className="loading-container">
      <div className="loading-spinner"></div>
      <p className="loading-text">Preparing your dashboard...</p>
    </div>
  );

  return (
    <div className="couple-page">
      <div className="couple-container">
        <section className="welcome-section">
          {eventInfo.bannerUrl && (
            <div className="welcome-banner">
              <img src={eventInfo.bannerUrl} alt="Event Banner" />
              <div className="banner-overlay"></div>
            </div>
          )}
          <img src="/logo.png" alt="Logo" className="welcome-logo" />
          <h1 className="welcome-heading">
            {eventInfo.title || 'Your Special Day'}
          </h1>
          <p className="welcome-subtitle">We’re thrilled to share this moment with you</p>
          <div className="welcome-buttons">
            <button
              className="action-btn"
              onClick={() => handleMenuAction('home')}
              title="Home"
            >
              <svg
                className="btn-icon"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="16px"
                height="16px"
                fill="#52443e"
              >
                <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
              </svg>
              Home
            </button>
            <button
              className="action-btn"
              onClick={() => handleMenuAction('view')}
              title="View Event"
            >
              <svg
                className="btn-icon"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="16px"
                height="16px"
                fill="#52443e"
              >
                <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
              </svg>
              View
            </button>
            <button
              className="action-btn"
              onClick={() => handleMenuAction('editCode')}
              title="Event Code"
            >
              <svg
                className="btn-icon"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="16px"
                height="16px"
                fill="#52443e"
              >
                <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
              </svg>
              Event Code
            </button>
            <button
              className="action-btn"
              onClick={() => handleMenuAction('editOptions')}
              title="Edit Options"
            >
              <svg
                className="btn-icon"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="16px"
                height="16px"
                fill="#52443e"
              >
                <path d="M4 10v4h4v-4H4zm6 0v4h4v-4h-4zm6 0v4h4v-4h-4zM4 4v4h4V4H4zm6 0v4h4V4h-4zm6 0v4h4V4h-4zm-12 12v4h4v-4H4zm6 0v4h4v-4h-4zm6 0v4h4v-4h-4z"/>
              </svg>
              Meals
            </button>
            <button
              className="action-btn"
              onClick={() => handleMenuAction('viewQR')}
              title="QR Code"
            >
              <svg
                className="btn-icon"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="16px"
                height="16px"
                fill="#52443e"
              >
                <path d="M3 3h8v8H3V3zm2 2v4h4V5H5zm-2 8h8v8H3v-8zm2 2v4h4v-4H5zm8-12h8v8h-8V3zm2 2v4h4V5h-4zm8 8h-2v2h2v2h-2v2h2v-2h2v-2h-2v-2zm-4 4h-2v2h2v-2zm-2-2h-2v2h2v-2zm4 4h2v2h-2v-2zm-8 0h2v2h-2v-2z"/>
              </svg>
              QR Code
            </button>
            <button
              className="action-btn logout-btn"
              onClick={() => handleMenuAction('logout')}
              title="Logout"
            >
              <svg
                className="btn-icon"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="16px"
                height="16px"
                fill="#52443e"
              >
                <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z"/>
              </svg>
              Logout
            </button>
          </div>
        </section>

        {showQRCode ? (
          <section className="qr-section">
            <h2 className="section-heading">Event QR Code</h2>
            <p className="section-description">Share this QR code with your guests to RSVP.</p>
            <div className="qr-container">
              <div className="qr-card">
                <QRCodeCanvas
                  value={`${window.location.origin}/rsvp/${eventId}`}
                  size={200}
                  level="H"
                  includeMargin={true}
                  className="qr-code"
                />
              </div>
              <div className="link-container">
                <input
                  type="text"
                  value={`${window.location.origin}/rsvp/${eventId}`}
                  readOnly
                  className="link-input"
                />
                <button className="copy-link-btn" onClick={handleCopyLink}>
                  Copy Link
                </button>
                <small className="link-hint">Guests can use this link to RSVP.</small>
              </div>
            </div>
            <div className="action-buttons">
              <button
                className="back-btn"
                onClick={() => setShowQRCode(false)}
              >
                Back
              </button>
            </div>
          </section>
        ) : editCodeMode ? (
          <section className="event-code-section">
            <div className="action-buttons">
              <button
                className="back-btn"
                onClick={() => setEditCodeMode(false)}
              >
                Back
              </button>
            </div>
            <h2 className="section-heading">Edit Event Code</h2>
            <p className="section-description">Set an optional code for private events.</p>
            <div className="form-group">
              <label className="edit-label">Event Code (optional, for private events)</label>
              <input
                type="text"
                placeholder="Enter event code (e.g., WEDDING2025)"
                value={eventInfo.eventCode}
                onChange={(e) => handleEventCodeChange(e.target.value)}
                className="edit-input"
              />
              <small className="form-hint">
                Leave blank for public event. Set a code to require guests to enter it on the RSVP page.
              </small>
            </div>
            <div className="action-buttons">
              <button className="save-btn" onClick={handleSaveEvent}>
                Save
              </button>
              {saveEventMessage && <span className="success-message">{saveEventMessage}</span>}
            </div>
          </section>
        ) : editOptionsMode ? (
          <section className="edit-options-section">
            <div className="action-buttons">
              <button
                className="back-btn"
                onClick={() => {
                  setEditOptionsMode(false);
                  setEditCodeMode(false);
                }}
              >
                Back
              </button>
            </div>
            <h2 className="section-heading">Edit RSVP Options</h2>
            <p className="section-description">Customize meal options and questions for your guests.</p>
            <div className="form-group">
              <div className="toggle-container">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={useMealOptions}
                    onChange={(e) => handleToggleMealOptions(e.target.checked)}
                    className="toggle-input"
                  />
                  <span className="toggle-slider"></span>
                  <span className="toggle-status">
                    Meal Options: {useMealOptions ? 'On' : 'Off'}
                  </span>
                </label>
                <small className="form-hint">
                  Enable to allow guests to select meal options when RSVPing. Disable to remove meal selections.
                </small>
                {toggleMessage && <span className="success-message">{toggleMessage}</span>}
              </div>
              {useMealOptions && (
                <div className="meal-options-container">
                  <h3 className="sub-section-heading">Meal Options</h3>
                  <p className="section-description">Set the meal options your guests can choose from when they RSVP.</p>
                  {mealOptions.map((option, idx) => (
                    <div key={idx} className="meal-group">
                      <input
                        type="text"
                        placeholder={`Meal Option ${idx + 1}`}
                        value={option}
                        onChange={(e) => handleMealChange(idx, e.target.value)}
                        className="edit-input"
                      />
                      <button
                        className="remove-btn"
                        onClick={() => removeMealOption(idx)}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  <div className="action-buttons">
                    <button className="add-btn" onClick={addMealOption} disabled={!useMealOptions}>
                      Add
                    </button>
                    <button className="save-btn" onClick={handleSaveMeals} disabled={!useMealOptions}>
                      Save
                    </button>
                    {saveMealsMessage && <span className="success-message">{saveMealsMessage}</span>}
                  </div>
                </div>
              )}
            </div>
            <div className="form-group">
              <h3 className="sub-section-heading">Custom RSVP Questions</h3>
              <p className="section-description">Add questions for your guests to answer when RSVPing.</p>
              {customQuestions.map((question, idx) => (
                <div key={idx} className="question-group">
                  <input
                    type="text"
                    placeholder={`Question #${idx + 1}`}
                    value={question}
                    onChange={(e) => handleQuestionChange(idx, e.target.value)}
                    className="edit-input"
                  />
                  <button
                    className="remove-btn"
                    onClick={() => removeQuestion(idx)}
                    disabled={customQuestions.length === 0}
                  >
                    Remove
                  </button>
                </div>
              ))}
              <div className="action-buttons">
                <button className="add-btn" onClick={addQuestion}>
                  Add
                </button>
                <button className="save-btn" onClick={handleSaveQuestions}>
                  Save
                </button>
                {saveQuestionsMessage && <span className="success-message">{saveQuestionsMessage}</span>}
              </div>
            </div>
          </section>
        ) : (
          <>
            <section className="summary-section">
              <h2 className="section-heading">RSVP Summary</h2>
              <div className="summary-stats">
                <div className="stat-card">
                  <span className="stat-label">Guests Responded</span>
                  <span className="stat-value">{totalGuestsResponded}</span>
                </div>
                <div className="stat-card">
                  <span className="stat-label">Guests Attending</span>
                  <span className="stat-value">{totalGuestsAttending}</span>
                </div>
                <div className="stat-card">
                  <span className="stat-label">Yes Responses</span>
                  <span className="stat-value">{yesResponses}</span>
                </div>
                <div className="stat-card">
                  <span className="stat-label">No Responses</span>
                  <span className="stat-value">{noResponses}</span>
                </div>
              </div>
              {useMealOptions && mealOptions?.length > 0 && (
                <>
                  <h3 className="sub-section-heading">Meal Selection Counts</h3>
                  <div className="meal-counts">
                    {Object.entries(mealCounts).map(([meal, count]) => (
                      <div key={meal} className="meal-card">
                        <span className="meal-name">{meal}</span>
                        <span className="meal-count">{count}</span>
                      </div>
                    ))}
                    {Object.keys(mealCounts).length === 0 && (
                      <p className="no-data">No meal selections available.</p>
                    )}
                  </div>
                </>
              )}
            </section>
            <section className="responses-section">
              <h2 className="section-heading">Guest Responses</h2>
              {deleteMessage && <span className="success-message">{deleteMessage}</span>}
              {responses.length === 0 && <p className="no-data">No RSVPs yet.</p>}
              {responses.length > 0 && (
                <>
                  <div className="responses-list">
                    {paginatedResponses.map((rsvp) => (
                      <div key={rsvp.id} className="response-card">
                        <div className="response-header">
                          <h3 className="response-name">{rsvp.guestName || 'N/A'}</h3>
                          <button
                            className="delete-btn"
                            onClick={() => handleDeleteRSVP(rsvp.id)}
                            title="Delete RSVP"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                              width="14px"
                              height="14px"
                              fill="#52443e"
                            >
                              <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                            </svg>
                          </button>
                        </div>
                        <div className="response-details">
                          <div className="detail-item">
                            <span className="detail-label">Attending:</span>
                            <span className="detail-value">{rsvp.isAttending ? 'Yes' : 'No'}</span>
                          </div>
                          <div className="detail-item">
                            <span className="detail-label">Guests:</span>
                            <span className="detail-value">
                              <ul className="guest-list">
                                <li>{rsvp.guestName || 'N/A'}</li>
                                {Array.isArray(rsvp.guestNames) && rsvp.guestNames.length > 0 && rsvp.guestNames.map((name, idx) => (
                                  name && name !== rsvp.guestName ? <li key={idx}>Guest {idx + 1}: {name}</li> : null
                                )).filter(Boolean)}
                              </ul>
                            </span>
                          </div>
                          <div className="detail-item">
                            <span className="detail-label">Number of Guests:</span>
                            <span className="detail-value">{rsvp.numGuests || 1}</span>
                          </div>
                          {useMealOptions && mealOptions?.length > 0 && Array.isArray(rsvp.mealSelections) && rsvp.mealSelections.length > 0 && (
                            <div className="detail-item">
                              <span className="detail-label">Meal Selection(s):</span>
                              <span className="detail-value">
                                <ul className="meal-list">
                                  {rsvp.mealSelections.map((meal, idx) => (
                                    <li key={idx}>Guest {idx + 1}: {meal || 'N/A'}</li>
                                  ))}
                                </ul>
                              </span>
                            </div>
                          )}
                          {customQuestions.map((question, i) => (
                            <div key={i} className="detail-item">
                              <span className="detail-label">{question || `Question ${i + 1}`}:</span>
                              <span className="detail-value">{rsvp.customAnswers?.[i] || '-'}</span>
                            </div>
                          ))}
                          {rsvp.optionalMessage && (
                            <div className="detail-item">
                              <span className="detail-label">Message:</span>
                              <span className="detail-value">{rsvp.optionalMessage}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  {totalPages > 1 && (
                    <div className="pagination">
                      <button
                        className="pagination-btn"
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </button>
                      <span className="pagination-info">
                        Page {currentPage} of {totalPages}
                      </span>
                      <button
                        className="pagination-btn"
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              )}
            </section>
          </>
        )}
        <footer className="couple-footer">
          <p className="footer-text">
            Created and developed by <a href="https://get-gs.com" target="_blank" rel="noopener noreferrer" className="footer-link">GS</a>
          </p>
        </footer>
      </div>
    </div>
  );
}
